import React from "react";
import {
    AggregateQueryControls,
    VisualizationControlStates,
    DataExportControls,
    VisualizationMode,
} from "./types";
import { VisualizationModeControls } from "./VisualizationModeSelector";
import { useVisualizationModeState } from "./useVisualizationModeState";
import { useAggregateQueryControls } from "./useAggregateQueryControls";
import { useVisualizationControlState } from "./useVisualizationControlState";
import { ApiQuery } from "~/modules/filters/types";

// // // //

interface TimeseriesStateManagerChildProps {
    controls: VisualizationControlStates;
    aggregateQuery: AggregateQueryControls;
    mode: VisualizationModeControls;
    appliedQuery: ApiQuery;
    dataExportControls: DataExportControls;
}

interface TimeseriesStateManagerProps {
    modes: VisualizationMode[];
    children: (childProps: TimeseriesStateManagerChildProps) => void;
}

/**
 * Manages and aggregates state for timeseries visualizations, including mode, visualization controls,
 * aggregate query controls, and data export options.
 */
export function TimeseriesStateManager(props: TimeseriesStateManagerProps) {
    const { children } = props;

    // Use custom hooks to manage state based on the active visualization mode.
    const modeState = useVisualizationModeState(props.modes); // Manages the active mode and provides functionality to change modes.
    const controlsState = useVisualizationControlState(modeState.activeMode); // Manages visualization control states like legend, navigator, etc.
    const aggregateQuery = useAggregateQueryControls(modeState.activeMode); // Manages state for aggregate query controls.

    // Constructs the applied query based on the current mode and selected aggregate query controls.
    const appliedQuery: ApiQuery = {
        ...modeState.activeMode.query,
        aggregate: {
            groupBy: aggregateQuery.groupBy.value,
            aggregateFunction: aggregateQuery.aggregateFunction.value,
            interval: aggregateQuery.interval.value,
            mode: modeState.activeMode.query.aggregate?.mode,
            // NOTE - we don't have a group-limit control yet because this shouldn't be user-configurable
            groupLimit: modeState.activeMode.query.aggregate?.groupLimit,
        },
    };

    // Determines the data export controls state, defaults to enabled if not specified in the active mode.
    const dataExportControls: DataExportControls = modeState.activeMode
        .dataExportControls
        ? modeState.activeMode.dataExportControls
        : { enabled: true, ignoreFeatureFlag: false };

    // Render prop pattern to pass down the aggregated state to children.
    return (
        <>
            {children({
                mode: modeState,
                controls: controlsState,
                aggregateQuery,
                appliedQuery,
                dataExportControls,
            })}
        </>
    );
}
